import { CheckCircle, Clear, CloudUpload, Download, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import get from "lodash/get";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import purchaseService from "../../services/purchase";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";
import PurchaseEditModal from "./PurchaseEditModal";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      No purchases found
    </Typography>
  </Box>
);

const PURCHASE_LIST_FILTER_KEY = "purchase-list-filters";

const getDefaultPurchaseListOptions = () => ({
  keyword: "",
  status: "",
  startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
  endDate: dayjs().format("YYYY-MM-DD"),
  page_number: 0,
});

const getInitialPurchaseListState = () => {
  const defaultOptions = getDefaultPurchaseListOptions();

  if (typeof window === "undefined") {
    return {
      options: defaultOptions,
      pageSize: 10,
    };
  }

  const savedState = window.sessionStorage.getItem(PURCHASE_LIST_FILTER_KEY);

  if (!savedState) {
    return {
      options: defaultOptions,
      pageSize: 10,
    };
  }

  try {
    const parsedState = JSON.parse(savedState);
    return {
      options: {
        ...defaultOptions,
        ...parsedState.options,
      },
      pageSize: parsedState.pageSize || 10,
    };
  } catch {
    return {
      options: defaultOptions,
      pageSize: 10,
    };
  }
};

export default function PurchaseList() {
  const navigate = useNavigate();
  const initialState = getInitialPurchaseListState();
  const [pageSize, setPageSize] = useState<number>(initialState.pageSize);
  const user = JSON.parse(Cookies.get("USER") || "{}");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadTargetId, setUploadTargetId] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalId, setEditModalId] = useState<number | null>(null);
  const [options, setOptions] = useState<any>(initialState.options);

  const notification = useNotification();

  const listPurchases = useQuery(
    ["purchaseList", options],
    ({ queryKey }) => purchaseService.getAllPurchases(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const uploadReceipt = useMutation(
    (file: File) => {
      if (!uploadTargetId) throw new Error("No target ID");
      return purchaseService.uploadPurchaseFile(uploadTargetId, file, "penerimaan barang");
    },
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Receipt uploaded successfully!",
          type: "success",
          position: "top",
        });
        setUploadTargetId(null);
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to upload receipt",
          type: "error",
          position: "top",
        });
        setUploadTargetId(null);
      },
    }
  );

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && uploadTargetId) {
      uploadReceipt.mutate(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const downloadPurchase = useMutation(purchaseService.downloadPurchase, {
    onSuccess: () => {
      notification.onOpen({
        message: "Purchase document was downloaded successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to download purchase",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const downloadPaymentProof = useMutation(purchaseService.downloadPaymentProof, {
    onSuccess: () => {
      notification.onOpen({
        message: "Payment proof was downloaded successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to download payment proof",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const exportPurchases = useMutation(purchaseService.exportPurchases, {
    onSuccess: () => {
      notification.onOpen({
        message: "Purchases exported successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to export purchases",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const handleDownload = (purchaseId: number) => {
    downloadPurchase.mutate(purchaseId);
  };

  const handleDownloadPaymentProof = (fileId: number) => {
    downloadPaymentProof.mutate(fileId);
  };

  const handleExportPurchases = () => {
    const values = form.getValues();

    exportPurchases.mutate({
      keyword: values.keyword,
      status: options.status,
      startDate: values.startDate,
      endDate: values.endDate,
    });
  };

  const handleDeletePurchase = (purchaseId: number) => {
    if (window.confirm("Are you sure you want to delete this purchase?")) {
      purchaseService
        .declinePurchase({ id: purchaseId, decline_reason: "", is_deleted: true })
        .then(() => {
          notification.onOpen({
            message: "Purchase deleted successfully!",
            type: "success",
            position: "top",
          });
          listPurchases.refetch();
        })
        .catch(() => {
          notification.onOpen({
            message: "Failed to delete purchase",
            type: "error",
            position: "top",
          });
        });
    }
  };

  const form = useForm({
    defaultValues: {
      keyword: initialState.options.keyword,
      startDate: initialState.options.startDate,
      endDate: initialState.options.endDate,
    },
  });

  useEffect(() => {
    window.sessionStorage.setItem(
      PURCHASE_LIST_FILTER_KEY,
      JSON.stringify({
        options,
        pageSize,
      })
    );
  }, [options, pageSize]);

  const onHandleSearch = useCallback((values: { keyword: string; startDate: string; endDate: string }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
      startDate: values.startDate,
      endDate: values.endDate,
      page_number: 0,
    }));
  }, []);

  return (
    <MainLayout>
      <Helmet>
        <title>Purchase Order</title>
      </Helmet>

      <Breadcrumb
        label="Purchase Order"
        breadcrumbs={[{ label: "Dashboard", href: "/app" }]}
      />

      {/* Only admin can access this page */}
      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" justifyContent="space-between" spacing={2} useFlexGap flexWrap="wrap">
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Controller
                name="keyword"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search purchases..."
                    {...field}
                    {...config.onEnter(() => {
                      form.handleSubmit(onHandleSearch)();
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          onClick={form.handleSubmit(onHandleSearch)}
                        >
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                name="startDate"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    variant="outlined"
                    size="small"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      field.onChange(e);
                      setOptions((prev: any) => ({ ...prev, startDate: e.target.value, page_number: 0 }));
                    }}
                  />
                )}
              />
              <Controller
                name="endDate"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="date"
                    variant="outlined"
                    size="small"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      field.onChange(e);
                      setOptions((prev: any) => ({ ...prev, endDate: e.target.value, page_number: 0 }));
                    }}
                  />
                )}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/app/purchase/create-purchase"
              >
                Create Purchase
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExportPurchases}
                disabled={exportPurchases.isLoading}
              >
                Export Purchases
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ mt: 2, width: "100%", overflowX: "auto" }}>
          <DataGrid
            autoHeight
            loading={listPurchases.isLoading}
            getRowId={(record) => record.id}
            rows={get(listPurchases, "data.data", [])}
            page={options.page_number || 0}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              {
                field: "no",
                headerName: "No",
                sortable: false,
                filterable: false,
                width: 80,
                renderCell: (params) => (
                  <Typography>
                    {(options.page_number || 0) * pageSize + params.api.getRowIndex(params.id) + 1}
                  </Typography>
                ),
              },
              {
                field: "purchase_no",
                headerName: "Purchase No",
                flex: 1,
                minWidth: 150,
                renderCell: (params) => (
                  <Button
                    variant="text"
                    onClick={() => navigate(`/app/purchase/detail/${params.row.id}`)}
                    sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
                  >
                    {params.value}
                  </Button>
                ),
              },
              {
                field: "usage_date",
                headerName: "Usage Date",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {params.value ? dayjs(params.value).format("DD/MM/YYYY") : "N/A"}
                  </Typography>
                ),
              },
              {
                field: "request_date",
                headerName: "Request Date",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {params.value ? dayjs(params.value).format("DD/MM/YYYY") : "N/A"}
                  </Typography>
                ),
              },
              {
                field: "created_by_name",
                headerName: "Requested By",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {params.value}
                  </Typography>
                ),
              },
              {
                field: "status",
                headerName: "Status",
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                  <Typography>
                    {params.value}
                  </Typography>
                ),
              },
              {
                field: "check",
                headerName: "Check",
                sortable: false,
                flex: 0.6,
                minWidth: 90,
                renderCell: (params) => (
                  String(params.row.status || "").toLowerCase() === "done" ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : null
                ),
              },
              {
                field: "id",
                headerName: "Actions",
                sortable: false,
                flex: 1,
                minWidth: 400,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" spacing={1}>
                      {params.row.has_payment_proof && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          onClick={() => handleDownloadPaymentProof(params.value)}
                        >
                          Payment proof
                        </Button>
                      )}
                      {user.role_id === 1 && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleDownload(params.value)}
                          >
                            Purchase PDF
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setEditModalId(params.value);
                              setEditModalOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                        </>
                      )}

                      {user.role_id === 1 && String(params.row.status || "").toLowerCase() !== "done" && (
                        <Tooltip title="Delete Purchase">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeletePurchase(params.value)}
                          >
                            <Clear />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Upload Bukti Penerimaan Barang">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => {
                            setUploadTargetId(params.value);
                            fileInputRef.current?.click();
                          }}
                        >
                          <CloudUpload />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              minWidth: 980,
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
              ".MuiDataGrid-columnHeaderTitleContainer": {
                backgroundColor: "#303c40",
                color: "white",
              },
              ".MuiDataGrid-columnHeader": {
                backgroundColor: "#303c40",
                color: "white",
              },
              ".MuiDataGrid-columnSeparator": {
                backgroundColor: "#303c40",
                color: "white",
              },
            }}
          />
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          />
        </Box>

        <PurchaseEditModal
          open={editModalOpen}
          id={editModalId}
          onClose={() => {
            setEditModalOpen(false);
            listPurchases.refetch();
          }}
        />
      </ProtectedPermissionPage>
    </MainLayout>
  );
}